import {useHead} from '@vueuse/head';
import site from '@/content/site';
import pages from '@/content/pages';
import {absoluteUrl} from '@/seo/schema';

/**
 * Renseigne toutes les balises de tête d'une page : titre, description, canonical, robots, Open Graph, Twitter et JSON-LD.
 * À appeler dans setup() de la vue (et non dans mounted) : les balises sont alors retirées à la sortie de la page.
 *
 * Le titre et la description viennent de src/content/pages.js, sauf pour les pages dynamiques (articles) qui les fournissent.
 *
 * @param {Object} options
 * @param {String} options.path          adresse de la page, ex. /blog (la balise canonical en découle)
 * @param {String} [options.title]
 * @param {String} [options.description]
 * @param {String} [options.image]       image de partage (1200 x 630), défaut : site.ogImage
 * @param {String} [options.type]        og:type, « website » ou « article »
 * @param {Boolean} [options.noindex]
 * @param {Array}  [options.graph]       nœuds JSON-LD de la page
 * @param {String} [options.published]   date de publication (articles)
 * @param {String} [options.modified]    date de modification (articles)
 */
export function useSeo(options) {
    const page = pages.find(entry => entry.path === options.path) || {};
    const title = options.title || page.title;
    const description = options.description || page.description;
    const noindex = options.noindex !== undefined ? options.noindex : !!page.noindex;
    const url = absoluteUrl(options.path);
    const image = absoluteUrl(options.image || site.ogImage);

    const meta = [
        {name: 'description', content: description},
        {name: 'robots', content: noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1'},
        {property: 'og:type', content: options.type || 'website'},
        {property: 'og:site_name', content: site.name},
        {property: 'og:locale', content: site.locale},
        {property: 'og:url', content: url},
        {property: 'og:title', content: title},
        {property: 'og:description', content: description},
        {property: 'og:image', content: image},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:title', content: title},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: image}
    ];
    if (options.published) {
        meta.push({property: 'article:published_time', content: options.published});
        meta.push({property: 'article:modified_time', content: options.modified || options.published});
        meta.push({property: 'article:author', content: absoluteUrl(site.author.path)});
    }

    const head = {
        title,
        meta,
        link: [{rel: 'canonical', href: url}]
    };
    if (options.graph && options.graph.length) {
        head.script = [{
            type: 'application/ld+json',
            children: JSON.stringify({'@context': 'https://schema.org', '@graph': options.graph})
        }];
    }
    useHead(head);
}
